import * as React from 'react';
import classNames from 'classnames';
import {Form, Field} from 'react-final-form';
import {withI18n, WithI18nProps} from '@shopify/react-i18n';
import {requiredValidation} from 'utilities';
import {submitSubscription} from 'utilities/subscriptionApi';
import {BackgroundColors} from 'components/UIKit/Types';
import {NOOP} from 'components/UIKit/utilities';
import {Button, Flex, Text, TextField} from 'components/UIKit';
import * as locales from './locales';
import style from './SubscriptionForm.scss';

export interface SubscriptionFormProps {
  authenticityToken: string;
  renderHeading?: () => React.ReactNode;
  renderSubhead?: () => React.ReactNode;
  disclaimer?: boolean;
  confirm?: boolean;
  inline?: boolean;
  centre?: boolean;
  className?: string;
  backgroundColor?: BackgroundColors;
}

interface SubscriptionState {
  success: boolean;
  error: boolean;
}

export class SubscriptionFormComponent extends React.Component<
  SubscriptionFormProps & WithI18nProps,
  SubscriptionState
> {
  state: SubscriptionState = {
    success: false,
    error: false,
  };

  handleSuccess = () => {
    this.setState({success: true});
  };

  handleError = () => {
    this.setState({error: true});
  };

  handleSubmit = (formValues) => {
    const {authenticityToken} = this.props;
    submitSubscription(formValues, authenticityToken)
      .then((response) => this[response.ok ? 'handleSuccess' : 'handleError']())
      .catch(this.handleError);
  };

  render() {
    const {
      inline = false,
      centre = true,
      confirm = false,
      className,
      disclaimer = false,
      renderHeading = NOOP,
      renderSubhead = NOOP,
      backgroundColor,
      i18n,
    } = this.props;
    const showForm = !this.state.success;
    const showSuccess = this.state.success;
    const formError = this.state.error && i18n.translate('error');

    return (
      <section
        aria-live="polite"
        className={classNames(style.container, className, {
          [style.inline]: inline,
          [style.centre]: centre,
        })}
      >
        <div
          aria-hidden={showSuccess}
          className={classNames({[style.visible]: showForm, [style.hidden]: showSuccess})}
        >
          <Form onSubmit={this.handleSubmit}>
            {({handleSubmit}) => (
              <form onSubmit={handleSubmit} className={style.form}>
                <Flex
                  className={inline && style.inline}
                  direction={inline ? 'row' : 'column'}
                  wrap="wrap"
                >
                  {renderHeading() || (
                    <Text text={i18n.translate('heading')} className={style.heading} inheritColor />
                  )}
                  {renderSubhead()}
                  <Flex
                    justifyContent="space-between"
                    alignItems="start"
                    className={style.inputButtonPair}
                  >
                    <Field name="email" validate={requiredValidation}>
                      {({input, meta}) => (
                        <TextField
                          className={classNames(!confirm && style.inlineInput)}
                          error={meta.error || formError}
                          label={i18n.translate('fields.email')}
                          touched={meta.touched}
                          value={input.value}
                          type="email"
                          helpText={disclaimer ? i18n.translate('disclaimer') : ''}
                          backgroundColor={backgroundColor}
                          floating
                          autocomplete="email"
                          ariaDescribedById="email_error"
                          {...input}
                        />
                      )}
                    </Field>
                    <Button type="submit" label={i18n.translate('fields.submit')} tall />
                  </Flex>
                </Flex>
              </form>
            )}
          </Form>
        </div>
        <div
          aria-hidden={showForm}
          className={classNames(style.successFeedback, {
            [style.hidden]: showForm,
            [style.visible]: showSuccess,
          })}
        >
          <Text type="h3" text={i18n.translate('success')} inheritColor align="center" />
        </div>
      </section>
    );
  }
}

export const SubscriptionForm = withI18n({
  id: 'SubscriptionForm',
  fallback: locales.en,
})(SubscriptionFormComponent);
